import React, { useMemo, useState } from "react";
import { Button, Divider, Flex, Form, Modal, Select, Tag, Typography, theme } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { SelectProps } from "antd";
import { CampoDeValor, llevaColor, llevaImagen } from "./CampoDeValor";
import { esElMismo, valoresParecidos } from "./valores-parecidos";
import type { CatalogAttribute } from "./types";

type Opcion = NonNullable<CatalogAttribute["options"]>[number];

/**
 * La muestra de una opción en la lista (xcons-docs#490): el color de un Color, el logo de una
 * Marca. Magento guarda el swatch como un hex (`#ff0000`) o como una imagen, y los dos
 * llegan en `image`. Sin muestra, la opción va sólo con su etiqueta.
 */
export const Muestra: React.FC<{ swatch: string }> = ({ swatch }) => {
  const { token } = theme.useToken();
  const caja: React.CSSProperties = {
    width: 20,
    height: 20,
    flexShrink: 0,
    borderRadius: token.borderRadiusXS,
    border: `1px solid ${token.colorBorderSecondary}`,
  };
  return /^#[0-9a-f]{3,8}$/i.test(swatch) ? (
    <span style={{ ...caja, background: swatch }} />
  ) : (
    <img src={swatch} alt="" style={{ ...caja, objectFit: "contain" }} />
  );
};

/**
 * Un valor creado desde una ficha que **todavía no revisó nadie** (xcons-docs#490): se marca
 * en la lista y en el campo, así quien aprueba el producto ve que también aprueba el valor.
 */
const ValorNuevo = () => (
  <Tag color="gold" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "16px" }}>
    Valor nuevo
  </Tag>
);

/**
 * El selector de un atributo con opciones en la ficha (xcons-docs#490). Se elige, no se tipea,
 * y al final de la lista va siempre **Crear valor**, en negrita y verde como "Crear contacto":
 * abre un popup con el valor y la miniatura para su imagen
 * en los que la llevan. Lo creado queda en el atributo
 * —para todos los productos y todas las personas— y elegido en el campo.
 *
 * Mientras se escribe, el popup ofrece **los valores parecidos** para elegir en vez de crear
 * uno nuevo, y frena el que es el mismo escrito distinto: el alta no pasa por aprobación,
 * así que es lo que evita los duplicados.
 *
 * Recibe `value` y `onChange` del `Form.Item`; el resto va al `Select`.
 */
export const SelectorDeOpcion: React.FC<
  Omit<SelectProps, "options"> & {
    attribute: CatalogAttribute;
    /** Las opciones ya armadas, con las borradas que el producto todavía tiene. */
    options: Array<{ value: number | string; label: string; disabled?: boolean }>;
    /** Crea el valor en el atributo y devuelve la opción, o `null` si no se pudo. */
    onCrear: (
      label: string,
      imagen: File | null,
      color: string | null,
    ) => Promise<Opcion | null>;
  }
> = ({ attribute, options, onCrear, value, onChange, disabled, ...resto }) => {
  const { token } = theme.useToken();
  const [abierto, setAbierto] = useState(false);
  const [creando, setCreando] = useState(false);
  const [popup, setPopup] = useState(false);
  const [formValor] = Form.useForm<{ label: string }>();
  const [imagen, setImagen] = useState<File | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const escrito = (Form.useWatch("label", formValor) as string | undefined) ?? "";

  const multiple = attribute.frontendInput === "multiselect";
  const etiqueta = attribute.label ?? attribute.code;
  /**
   * **Las opciones por valor, armadas una vez.** La lista no es virtual, así que al abrirla se
   * dibujan todas, y cada una buscaba su muestra y su marca recorriendo la lista entera: con
   * los 1.314 colores eran ~3,4 millones de comparaciones por apertura, con Tamaño ~45 millones.
   */
  const porValor = useMemo(
    () => new Map((attribute.options ?? []).map((o) => [String(o.value), o])),
    [attribute.options],
  );
  const opcionDe = (v: unknown) => porValor.get(String(v));
  const muestraDe = (v: unknown) => opcionDe(v)?.image ?? null;
  const conMuestras = useMemo(
    () => !!attribute.options?.some((o) => o.image),
    [attribute.options],
  );
  const parecidos = valoresParecidos(escrito, attribute.options ?? []);
  const elMismo = attribute.options?.find((o) => esElMismo(o.label, escrito));

  /** Elegir uno que ya existe desde el popup, en vez de crearlo. */
  const elegirExistente = (o: Opcion) => {
    const elegido = multiple ? [...((value as unknown[]) ?? []), o.value] : o.value;
    onChange?.(elegido, o as never);
    setPopup(false);
  };

  const abrirPopup = () => {
    setAbierto(false);
    setImagen(null);
    setColor(null);
    setPopup(true);
  };

  const crear = async () => {
    const { label } = await formValor.validateFields();
    setCreando(true);
    try {
      const opcion = await onCrear(label.trim(), imagen, color);
      if (!opcion) return;
      const elegido = multiple
        ? [...((value as unknown[]) ?? []), opcion.value]
        : opcion.value;
      onChange?.(elegido, opcion as never);
      setPopup(false);
    } finally {
      setCreando(false);
    }
  };

  const select = (
    <Select
      {...resto}
      value={value}
      onChange={onChange}
      disabled={disabled}
      // Un desplegable común, sin cruz para vaciarlo ni buscador (xcons-docs#499): se elige
      // de la lista, no se tipea.
      showSearch={false}
      // **Con lista virtual.** Se había apagado porque el click caía en otra opción (Color
      // elegía uno cualquiera), pero sin ella se dibujaban todas al abrir: Tamaño, con 4.785,
      // tardaba ~1 s; virtual abre en ~20 ms. Las filas miden todas 32 px, con muestra o sin.
      virtual
      mode={multiple ? "multiple" : undefined}
      open={abierto}
      onDropdownVisibleChange={setAbierto}
      style={{ flex: 1, minWidth: 0 }}
      options={options}
      optionRender={(o) => {
        const swatch = conMuestras ? muestraDe(o.value) : null;
        return (
          <Flex align="center" gap={8}>
            {swatch && <Muestra swatch={swatch} />}
            {o.label}
            {opcionDe(o.value)?.nuevo && <ValorNuevo />}
          </Flex>
        );
      }}
      // El elegido también lleva su muestra y la marca: es lo que ve quien revisa el producto
      // (xcons-docs#499).
      labelRender={(l) => {
        const swatch = conMuestras ? muestraDe(l.value) : null;
        return (
          <Flex align="center" gap={8} component="span">
            {swatch && <Muestra swatch={swatch} />}
            {l.label}
            {opcionDe(l.value)?.nuevo && <ValorNuevo />}
          </Flex>
        );
      }}
      dropdownRender={(menu) => (
        <>
          {menu}
          <Divider style={{ margin: "4px 0" }} />
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={abrirPopup}
            style={{ fontWeight: 700, color: token.colorPrimary }}
          >
            Crear valor
          </Button>
        </>
      )}
    />
  );

  return (
    <>
      <Flex>{select}</Flex>

      <Modal
        open={popup}
        title={`Crear valor de ${etiqueta}`}
        okText="Crear"
        cancelText="Cancelar"
        confirmLoading={creando}
        onOk={() => void crear()}
        onCancel={() => setPopup(false)}
        destroyOnClose
      >
        <Form
          form={formValor}
          layout="vertical"
          preserve={false}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="label"
            label="Valor"
            extra="Queda disponible para todos los productos."
            rules={[
              { required: true, whitespace: true, message: "Escribí el valor" },
              {
                // El mismo escrito distinto ("gris tópo" y "Gris Topo") también frena.
                validator: (_, v?: string) =>
                  v && attribute.options?.some((o) => esElMismo(o.label, v))
                    ? Promise.reject(new Error(`Ya existe en ${etiqueta}`))
                    : Promise.resolve(),
              },
            ]}
          >
            <CampoDeValor
              autoFocus
              placeholder={`Nombre del valor de ${etiqueta}`}
              conImagen={llevaImagen(attribute)}
              conColor={llevaColor(attribute)}
              imagen={imagen}
              onImagen={setImagen}
              color={color}
              onColor={setColor}
              onPressEnter={() => void crear()}
            />
          </Form.Item>
          {/* Lo que ya existe, para elegirlo en vez de crearlo. */}
          {(elMismo || parecidos.length > 0) && (
            <Flex vertical gap={4} style={{ marginTop: -8 }}>
              <Typography.Text type="secondary">
                {elMismo ? "Ya existe:" : "¿Quisiste decir…?"}
              </Typography.Text>
              <Flex wrap gap={8}>
                {(elMismo ? [elMismo] : parecidos).map((o) => (
                  <Button key={o.value} size="small" onClick={() => elegirExistente(o)}>
                    {o.image && <Muestra swatch={o.image} />}
                    {o.label}
                  </Button>
                ))}
              </Flex>
            </Flex>
          )}
        </Form>
      </Modal>
    </>
  );
};
